import { AlertTriangle } from 'lucide-react';
import VideoLesson from './VideoLesson';
import PdfLesson from './PdfLesson';
import TextLesson from './TextLesson';
import InteractiveLesson from './InteractiveLesson';
import QuizLesson from './QuizLesson';
import ExamLesson from './ExamLesson';
import FlashcardLesson from './FlashcardLesson';
import MatchingLesson from './MatchingLesson';
import DragDropLesson from './DragDropLesson';
import DialogueLesson from './DialogueLesson';           // Faz 4d
import FillInBlankLesson from './FillInBlankLesson';     // Faz 4d
import SwipeVocabularyLesson from './SwipeVocabularyLesson'; // Faz 4d
import TimeRaceLesson from './TimeRaceLesson';           // Faz 4d

/**
 * @typedef {Object} Lesson
 * @property {string} lesson_id
 * @property {'video'|'pdf'|'text'|'interactive'|'quiz'|'exam'|'flashcard'|'matching'|'drag_drop'|'dialogue'|'fill_in_blank'|'swipe'|'time_race'} lesson_type
 * @property {string} title
 * @property {string} [title_en]
 * @property {string} [description]
 * @property {string} [description_en]
 * @property {string} [content]
 * @property {string} [vimeo_url]
 * @property {string} [vimeo_video_id]
 * @property {string} [pdf_file_id]
 * @property {string} [pdf_filename]
 * @property {Array}  [quiz_questions]
 * @property {number} [quiz_passing_score]
 * @property {number} [video_min_watch_percent]
 * @property {boolean} [materials_required]
 * @property {Array}  [materials]
 */

/**
 * Kayitli lesson tipi bileşenleri.
 * Faz 4c: flashcard, matching, drag_drop
 * Faz 4d: dialogue, fill_in_blank, swipe, time_race
 */
const REGISTRY = {
  video: VideoLesson,
  pdf: PdfLesson,
  text: TextLesson,
  interactive: InteractiveLesson,
  quiz: QuizLesson,
  exam: ExamLesson,
  flashcard: FlashcardLesson,          // Faz 4c
  matching: MatchingLesson,            // Faz 4c
  drag_drop: DragDropLesson,           // Faz 4c
  dialogue: DialogueLesson,            // Faz 4d
  fill_in_blank: FillInBlankLesson,    // Faz 4d
  swipe: SwipeVocabularyLesson,        // Faz 4d
  time_race: TimeRaceLesson,           // Faz 4d
};

/**
 * Desteklenmeyen ders tipleri icin fallback.
 */
function FallbackLesson({ lesson }) {
  return (
    <div className="p-8 text-center text-gray-500" data-testid="fallback-lesson">
      <AlertTriangle size={32} className="mx-auto mb-3 text-amber-500" />
      <p className="font-semibold">
        {`Bu ders tipi henuz desteklenmiyor: ${lesson.lesson_type}`}
      </p>
      <p className="text-sm mt-1 text-gray-400">
        {`This lesson type is not yet supported: ${lesson.lesson_type}`}
      </p>
    </div>
  );
}

/**
 * LessonDispatcher — lesson.lesson_type'a gore dogru bileşeni secip render eder.
 *
 * @param {{
 *   lesson: Lesson,
 *   courseId: string,
 *   onComplete: Function,
 *   completedLessonIds: string[],
 *   lessons: Lesson[],
 *   nextLesson: Lesson|null,
 * }} props
 */
export default function LessonDispatcher({ lesson, courseId, onComplete, completedLessonIds, lessons, nextLesson }) {
  const lessonType = lesson?.lesson_type || 'video';
  const Component = REGISTRY[lessonType] ?? FallbackLesson;

  return (
    <Component
      lesson={lesson}
      courseId={courseId}
      onComplete={onComplete}
      completedLessonIds={completedLessonIds}
      lessons={lessons}
      nextLesson={nextLesson}
    />
  );
}
